import { useQuery } from '@tanstack/react-query';
import { getIntegrationDisplayName } from '@aica/core';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { useOrganization } from '@/contexts/RoleContext';
import type { SourceRole } from '@aica/api-types/schemas';

export interface ActiveSource {
  /** Ярлык вендора: 'bitrix24', 'binotel', 'public_api', … */
  vendor: string;
  /** Бренд из каталога интеграций. Не переводится. */
  name: string;
  /**
   * Чем установка служит организации.
   *
   * `audio_source` — телефония отдаёт только запись разговора, записями
   * владеет CRM. Подключена, но приёма через неё нет: тот, кто гасит по этому
   * ответу баннер «источник не подключён», обязан различать эти два случая.
   *
   * `.nullish` по смыслу: ответ, собранный до появления поля, переживает
   * деплой в кеше react-query. Отсутствие роли читаем как источник записей —
   * так вело себя всё до гибридного режима.
   */
  role: SourceRole;
}

export interface ActiveSourceState {
  /** Подключённый источник, `null` — не подключено ничего. */
  source: ActiveSource | null;
  isLoading: boolean;
}

/**
 * Через что организация получает записи — один ответ на всех, кто об этом
 * спрашивает.
 *
 * Ключ запроса общий с `DataSourceBanner`, поэтому два баннера в одном шелле
 * стоят одного сетевого вызова, а не двух: react-query отдаёт второму
 * потребителю тот же кеш.
 *
 * Только портал: под `/embedded/*` маршрут `/api/v1/sources/*` не смонтирован
 * вовсе. Внутри CRM ту же правду везёт сводка бэклога
 * (`BacklogSummary.source`) — см. `ProcessingToggleBanner`.
 */
export function useActiveSource(): ActiveSourceState {
  const { authenticatedFetch } = useAuthFetch();
  const { selectedOrgId } = useOrganization();

  const { data, isLoading } = useQuery({
    // Scope to selectedOrgId so org switches refetch instead of returning
    // the previous org's cached result.
    queryKey: ['sources', 'active', selectedOrgId],
    enabled: !!selectedOrgId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const result = await authenticatedFetch<{
        data: { vendor: string; role?: SourceRole | null } | null;
      }>('/api/v1/sources/active');
      return result?.data ?? null;
    },
  });

  return {
    source: data
      ? {
          vendor: data.vendor,
          name: getIntegrationDisplayName(data.vendor),
          role: data.role ?? 'record_source',
        }
      : null,
    isLoading,
  };
}
